<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';

import { useCheckTaskPrimaryAction } from '@/composables/useCheckTaskPrimaryAction';
import { relatedObjectToYardSpot } from '@/helpers/checkTaskDetailsTitle';
import { SpotStatus } from '@nova/core';
import YardSpotPresenterV2 from '@satellite/components/elements/presenters/YardSpotPresenterV2.vue';
import { useYardSpotPresenter } from '@satellite/composables/useYardSpotPresenter';
import { useYardSpotStatus } from '@satellite/composables/useYardSpotStatus';
import {
  isTaskInProgress,
  getSpotStatusIcon,
  getSpotStatusColor
} from '@satellite/helpers/yardTaskUtils';

import type { CheckTaskViewData, ISpot } from '@nova/core';

const props = defineProps<{
  task: CheckTaskViewData;
}>();

const task = toRef(props, 'task');
const { t } = useI18n();
const { triggerPrimaryAction } = useCheckTaskPrimaryAction();
const relatedObject = computed(() => task.value.relatedObject);
const spotYard = computed(() => relatedObjectToYardSpot(relatedObject.value) ?? null);
const spotId = computed(() => relatedObject.value?.id ?? null);

const { data: liveSpot } = useYardSpotPresenter({
  spotId,
  placeholderSpotData: spotYard,
  headless: true
});
const yardSpotStatus = useYardSpotStatus();

const primaryDisabled = computed(
  () =>
    !isTaskInProgress(task.value) ||
    !task.value.id ||
    !task.value.checkId ||
    !relatedObject.value?.id
);

const spotOccupancyStatus = computed(() => {
  if (!liveSpot.value) return SpotStatus.UNKNOWN;
  return yardSpotStatus.getRawStatus(liveSpot.value as ISpot);
});

const spotStatusIcon = computed(() => getSpotStatusIcon(spotOccupancyStatus.value));
const spotStatusColor = computed(() => getSpotStatusColor(spotOccupancyStatus.value));
const spotStatusLabel = computed(() => {
  const status = spotOccupancyStatus.value;
  const key = status === SpotStatus.ASSIGNED ? 'occupied' : status;
  return t(`checkTaskDetails.spotOccupancy.${key}`);
});

function onPrimary() {
  triggerPrimaryAction(task.value);
}
</script>

<template>
  <v-card variant="flat" border class="text-center" data-testid="check-task-details-title">
    <v-card-text>
      <div class="check-task-title d-flex align-center justify-center gap-2">
        <v-icon size="small" icon="mdi-map-marker-outline" />
        <yard-spot-presenter-v2
          v-if="spotId"
          :spot-id="spotId"
          :placeholder-spot-data="spotYard"
          :display-fields="['area', 'code']"
          :area-props="{ class: 'text--color-text-secondary' }"
          :spot-code-props="{ class: 'text--color-text-secondary' }"
          size="large" />
        <v-chip size="small" variant="tonal">
          {{ t('checkTaskDetails.entityChips.spot') }}
        </v-chip>
      </div>

      <v-btn
        class="mt-6 mb-2 typography-button-lg"
        size="large"
        color="primary"
        :disabled="primaryDisabled"
        append-icon="mdi-arrow-right"
        @click="onPrimary">
        {{ t('checkTaskDetails.buttons.spotDetails') }}
      </v-btn>

      <v-divider class="my-4" />
      <div class="d-flex align-center justify-center gap-2">
        <v-icon size="16" :color="spotStatusColor" :icon="spotStatusIcon" />
        <span class="typography-body-sm text-medium-emphasis">{{ spotStatusLabel }}</span>
      </div>
    </v-card-text>
  </v-card>
</template>
